import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Link } from "react-router-dom";
import { IMAGES } from "@/data";
import { useLang } from "@/context/LanguageContext";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({ y: "0%", transition: { duration: 1, delay: 0.3 + i * 0.14, ease: [0.16, 1, 0.3, 1] } }),
};

export const Hero = () => {
  const { t } = useLang();
  const h = t.hero;
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section ref={ref} id="top" className="relative min-h-screen w-full overflow-hidden pt-20">
      <div className="max-w-7xl mx-auto px-6 md:px-12 pt-16 md:pt-24 grid lg:grid-cols-12 gap-10 items-end">
        <div className="lg:col-span-7">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.15, duration: 0.8 }} className="overline text-[#555] mb-8 flex items-center gap-3">
            <span className="inline-block h-px w-10 bg-[#C4A484]" />
            {h.overline}
          </motion.p>

          <h1 className="font-serif text-[3.4rem] leading-[0.92] sm:text-7xl lg:text-[7rem] lg:leading-[0.88] tracking-tight text-[#0D2A27]">
            {[h.line1, h.line2].map((txt, i) => (
              <span key={i} className="block overflow-hidden">
                <motion.span custom={i} variants={line} initial="hidden" animate="show" className="block">{txt}</motion.span>
              </span>
            ))}
            <span className="block overflow-hidden">
              <motion.span custom={2} variants={line} initial="hidden" animate="show" className="block">
                {h.line3pre}<span className="italic text-[#C4A484]">{h.line3accent}</span>
              </motion.span>
            </span>
          </h1>

          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.9 }} className="mt-8 max-w-md text-base md:text-lg text-[#555] leading-relaxed">
            {h.para}
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05, duration: 0.9 }} className="mt-10 flex flex-wrap items-center gap-4">
            <Link to="/#contact" data-testid="hero-cta-btn" className="group inline-flex items-center gap-2 rounded-full bg-[#0D2A27] px-8 py-4 text-sm font-semibold text-[#F7F6F2] transition-colors duration-300 hover:bg-[#163B37]">
              {h.cta}
              <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
            </Link>
            <Link to="/#services" data-testid="hero-secondary-btn" className="inline-flex items-center gap-2 rounded-full border border-[#0D2A27]/25 px-8 py-4 text-sm font-semibold text-[#0D2A27] transition-colors duration-300 hover:bg-[#0D2A27] hover:text-[#F7F6F2]">
              {h.secondary}
            </Link>
          </motion.div>
        </div>

        <motion.div initial={{ opacity: 0, y: 60 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6, duration: 1.1, ease: [0.16, 1, 0.3, 1] }} className="lg:col-span-5 relative">
          <div className="relative h-[420px] md:h-[540px] overflow-hidden rounded-t-[999px] border border-[#E2E0D8]">
            <motion.img src={IMAGES.heroClinic} alt="Modern accredited clinic interior" style={{ y, scale }} className="absolute inset-0 h-[120%] w-full object-cover" />
          </div>
          <div className="absolute -left-4 bottom-8 bg-[#F7F6F2] border border-[#E2E0D8] px-5 py-4 shadow-sm">
            <p className="font-serif text-3xl text-[#0D2A27] leading-none">{h.statNum}</p>
            <p className="text-xs text-[#555] mt-1 max-w-[9rem]">{h.statLabel}</p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
